export type ResourceDraftContext = {
  account: string;
  actor: string;
  location: string;
  kind: "product" | "stock" | "service" | "offering";
  record: string;
  revision: string;
};

const prefix = "garageclerk:resource-draft:";
const lifetime = 24 * 60 * 60 * 1000;
const fields: Record<ResourceDraftContext["kind"], readonly string[]> = {
  product: ["name", "unit"],
  stock: ["quantity", "reason"],
  service: ["name", "description", "offer_at_location"],
  offering: [
    "price",
    "currency",
    "estimated_minutes",
    "tax_rate",
    "tax_label",
    "tax_mode",
    "available",
  ],
};
const key = (context: ResourceDraftContext) =>
  prefix +
  JSON.stringify([
    context.account,
    context.actor,
    context.location,
    context.kind,
    context.record,
    context.revision,
  ]);

export function restoreResourceDraft<T extends Record<string, string>>(
  context: ResourceDraftContext,
  fallback: T,
): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = sessionStorage.getItem(key(context));
    if (!raw) return fallback;
    if (raw.length > 32 * 1024) {
      forgetResourceDraft(context);
      return fallback;
    }
    const saved = JSON.parse(raw);
    if (
      !saved ||
      typeof saved.expires !== "number" ||
      saved.expires <= Date.now() ||
      !saved.values ||
      typeof saved.values !== "object"
    ) {
      forgetResourceDraft(context);
      return fallback;
    }
    const values = { ...fallback };
    for (const field of fields[context.kind]) {
      if (field in values && typeof saved.values[field] === "string")
        values[field as keyof T] = saved.values[field] as T[keyof T];
    }
    return values;
  } catch {
    forgetResourceDraft(context);
    return fallback;
  }
}

export function rememberResourceDraft(
  context: ResourceDraftContext,
  values: Record<string, string>,
): boolean {
  try {
    const allowed = Object.fromEntries(
      fields[context.kind]
        .filter((field) => typeof values[field] === "string")
        .map((field) => [field, values[field]]),
    );
    const raw = JSON.stringify({
      expires: Date.now() + lifetime,
      values: allowed,
    });
    if (raw.length > 32 * 1024) return false;
    const keys = Object.keys(sessionStorage).filter(
      (item) => item.startsWith(prefix) && item !== key(context),
    );
    for (const old of keys.slice(0, Math.max(0, keys.length - 19)))
      sessionStorage.removeItem(old);
    sessionStorage.setItem(key(context), raw);
    return true;
  } catch {
    return false;
  }
}

export function forgetResourceDraft(context: ResourceDraftContext) {
  try {
    sessionStorage.removeItem(key(context));
  } catch {
    // The editor still works when browser storage is restricted.
  }
}
